import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import type { WhoamiResponse } from "@layer/dashboard/types";
import {
  pageAllowed,
  pageForRoute,
} from "@layer/dashboard/page-registry";
import { api, ApiError } from "../lib/api";
import { setStudioTimeZone, studioTimeZone } from "../lib/studio-tz";
import { setCancellationDeadlineHours } from "../lib/studio-policy";
import { RealtimeProvider } from "../lib/realtime";
import {
  classifySessionLoss,
  handleSessionLost,
  registerSessionLostNavigator,
  signOutAndLeave,
} from "../lib/session-lost";
import { carriedInnerPath, chooserDecision } from "../lib/studio-chooser";
import { browserStorage, rememberLogin } from "../lib/saved-logins";
import { readMastheadBarOpen, writeMastheadBarOpen } from "../lib/masthead-bar";
import { studioPath } from "../lib/studio-scope";
import { StudioChooser } from "./StudioChooser";
import { ThemeToggle } from "./ThemeToggle";

interface WhoamiState {
  readonly loading: boolean;
  readonly data: WhoamiResponse | null;
  readonly error: string | null;
}

/**
 * The viewer's effective page grant, folded from whoami's two fields into the
 * one thing every consumer here actually asks.
 *
 * `page_access_resolved: false` means the server could not tell who the viewer
 * is. Fail-closed by Mario's decision, so that collapses to the EMPTY grant —
 * Home and nothing else — rather than to `null` (unrestricted). Getting this
 * fold backwards is the single line that would turn the whole feature into a
 * no-op, which is why it is one named function with this comment on it.
 */
function effectiveGrant(
  data: WhoamiResponse | null,
): ReadonlyArray<string> | null {
  if (data === null) return null;
  if (!data.page_access_resolved) return [];
  return data.visible_pages;
}

interface NavItem {
  readonly to: string;
  readonly label: string;
  /** `/` matches every route unless the NavLink is told to match exactly. */
  readonly end?: boolean;
}

/** A More entry carries one short line saying what the page holds. */
interface MoreNavItem extends NavItem {
  readonly description: string;
}

interface MoreNavGroup {
  readonly heading: string;
  readonly items: ReadonlyArray<MoreNavItem>;
}

/**
 * Migration 178 — which nav entries this viewer may see.
 *
 * DERIVED from the shared registry rather than a second list of ids beside the
 * nav: `pageForRoute` maps the link's own `to` back to its page, so a nav
 * entry cannot drift out of step with the gate that will refuse its endpoint.
 * A link whose route belongs to no page is KEPT — the registry-completeness
 * test makes that state unreachable, and hiding a link on a technicality would
 * be a worse failure than showing one.
 *
 * `granted === null` is unrestricted and returns the list untouched, which is
 * every viewer's state until someone restricts them.
 */
function visibleNav<T extends NavItem>(
  items: ReadonlyArray<T>,
  granted: ReadonlyArray<string> | null,
): ReadonlyArray<T> {
  if (granted === null) return items;
  return items.filter((n) => {
    const page = pageForRoute(n.to);
    return page === null ? true : pageAllowed(granted, page.id);
  });
}

// Primary nav — grouped by what the page is FOR, with a gap between groups.
// The groups are the structure, not decoration: overview, then the two things
// that change hour to hour, then the work queues, then the read-and-configure
// surfaces. Inbox, PR, Decisions, Automations and Agents moved into More on
// 2026-10-24 (Mario), so the row holds only the everyday pages.
const PRIMARY_NAV_GROUPS: ReadonlyArray<ReadonlyArray<NavItem>> = [
  [{ to: "/", label: "Home", end: true }],
  [
    { to: "/calendar", label: "Calendar" },
    { to: "/members", label: "Members" },
  ],
  [
    { to: "/tasks", label: "Tasks" },
    { to: "/flows", label: "Flows" },
  ],
  [
    { to: "/analytics", label: "Analytics" },
    { to: "/assets", label: "Assets" },
    { to: "/data", label: "Data" },
    { to: "/config", label: "Config" },
  ],
];

const PRIMARY_NAV: ReadonlyArray<NavItem> = PRIMARY_NAV_GROUPS.flat();

// Everything else lives behind the "More" dropdown, which sits immediately
// after the last primary link rather than pinned to the far edge. It opens as
// a panel of headed groups, each page with one line saying what it holds
// (Mario, 2026-10-24: "a properly designed dropdown … organized into groups.
// Each group has a heading and a super short description of what each page
// contains"). Keep each description to a few words: it is read at a glance.
const MORE_NAV_GROUPS: ReadonlyArray<MoreNavGroup> = [
  {
    heading: "Inbox & outreach",
    items: [
      { to: "/inbox", label: "Inbox", description: "Studio mail, sorted into matters" },
      { to: "/pr", label: "PR", description: "Press, influencers and partners" },
      { to: "/decisions", label: "Decisions", description: "Questions the admins vote on" },
    ],
  },
  {
    heading: "Marketing",
    items: [
      { to: "/campaigns", label: "Campaigns", description: "Email newsletters and their results" },
      { to: "/newsletter", label: "Newsletter", description: "Send an article on WhatsApp" },
      { to: "/email-templates", label: "Emails", description: "Every email the studio sends" },
      // A funnel campaign is written once here and then watched from /inbox.
      { to: "/instagram-funnel", label: "IG funnel", description: "Instagram comment-to-DM campaigns" },
      { to: "/reports", label: "Reports", description: "Dormant members, expiring credits" },
    ],
  },
  {
    heading: "Team",
    items: [
      { to: "/rota", label: "Rota", description: "Trainers' monthly schedules" },
      { to: "/payroll", label: "Payroll", description: "Monthly trainer pay runs" },
      { to: "/trainer-performance", label: "Trainer performance", description: "Each trainer's month, for the 1:1" },
    ],
  },
  {
    heading: "AI & automation",
    items: [
      { to: "/agents", label: "Agents", description: "Every AI agent and its work" },
      { to: "/automations", label: "Automations", description: "Scheduled jobs that report to staff" },
      { to: "/efficiency", label: "Efficiency", description: "How well the chat agent answers" },
      { to: "/preload", label: "Preload", description: "What each assistant is told" },
    ],
  },
  {
    heading: "System",
    items: [
      { to: "/status", label: "Status", description: "Health of everything Clavis does" },
      { to: "/errors", label: "Errors", description: "Defects found, with fix prompts" },
      { to: "/catalog", label: "Catalog", description: "Actions, connectors and sagas" },
      { to: "/mcp", label: "MCP", description: "Actions AI clients can call" },
      { to: "/cli", label: "CLI", description: "Command-line reference" },
      { to: "/whatsapp-signup", label: "WA signup", description: "Connect a WhatsApp number" },
    ],
  },
];

const MORE_NAV: ReadonlyArray<MoreNavItem> = MORE_NAV_GROUPS.flatMap((g) => g.items);

// ─── Mobile navigation ──────────────────────────────────────────────────
// On phones the desktop bar (the nav pills + the "More" panel) is replaced by
// three equal-width tabs: Calendar, Members, and "More" — where "More" is a
// full-screen sheet holding EVERYTHING else (every other primary link, the
// desktop More items, the studio's face + the theme switch + the studio
// switcher, plus the signed-in identity + Logout). The tabs are the phone's
// whole masthead. These two tabs are the direct destinations; the rest
// funnel through the sheet.
const MOBILE_TAB_TARGETS = new Set(["/calendar", "/members"]);
const MOBILE_MORE_NAV: ReadonlyArray<NavItem> = [
  ...PRIMARY_NAV.filter((n) => !MOBILE_TAB_TARGETS.has(n.to)),
  ...MORE_NAV,
];

/**
 * Refuses to render a page the viewer was not granted (migration 178).
 *
 * Two outcomes, deliberately different:
 *
 *   • RESTRICTED — they are simply not granted this page. Redirect to `/`
 *     (always allowed) with `replace`, so the Back button does not bounce them
 *     between the blocked URL and Home forever.
 *   • UNRESOLVED — we could not tell who they are. That is a broken
 *     `login_email` binding (migration 177), NOT a permissions decision, and
 *     telling someone "you don't have access" when the truth is "we don't know
 *     who you are" sends them to the wrong person for help. So this case gets a
 *     message naming the real problem and where it is fixed.
 *
 * A route belonging to no page renders — the registry-completeness test makes
 * that unreachable, and blocking on a technicality would be the worse failure.
 */
function RouteGuard({
  grant,
  resolved,
  children,
}: {
  readonly grant: ReadonlyArray<string> | null;
  readonly resolved: boolean;
  readonly children: React.ReactNode;
}): JSX.Element {
  const location = useLocation();
  const page = pageForRoute(location.pathname);
  if (page === null || pageAllowed(grant, page.id)) {
    return <>{children}</>;
  }
  if (!resolved) {
    return (
      <div className="page-blocked">
        <h1 className="page-title">We can&apos;t tell who you are</h1>
        <p>
          Your login isn&apos;t linked to a staff record, so the dashboard
          can&apos;t work out which pages you should see and is showing you none
          of them. This is a setup gap, not a permission you&apos;re missing.
        </p>
        <p className="subtle">
          Ask an admin to open <strong>Config → Staff allowlist</strong> and put
          your sign-in address in the <strong>login email</strong> column for
          your row.
        </p>
      </div>
    );
  }
  return <Navigate to="/" replace />;
}

const WhoamiContext = createContext<WhoamiState>({
  loading: true,
  data: null,
  error: null,
});

export function useWhoami(): WhoamiState {
  return useContext(WhoamiContext);
}

export function DashboardLayout(): JSX.Element {
  const [state, setState] = useState<WhoamiState>({
    loading: true,
    data: null,
    error: null,
  });
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  // Desktop: the bar above the nav pills, folded away until its icon pill
  // opens it (`lib/masthead-bar.ts`). Phones never show it.
  const [barOpen, setBarOpen] = useState<boolean>(() =>
    readMastheadBarOpen(browserStorage()),
  );
  const toggleBar = (): void => {
    const next = !barOpen;
    writeMastheadBarOpen(browserStorage(), next);
    setBarOpen(next);
  };
  const moreRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close both "More" surfaces whenever the route changes (clicking a link
  // inside either navigates, which should dismiss the menu/sheet).
  useEffect(() => {
    setMoreOpen(false);
    setMobileMoreOpen(false);
  }, [location.pathname]);

  // Close the "More" panel on an outside click, or on Escape — it covers the
  // top of the page, so the keyboard needs a way out that is not a link.
  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent): void => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  // Migration 178 — the viewer's grant, and the three nav lists filtered by it.
  // Derived on every render rather than memoised: whoami lands once and the
  // lists are ~24 items, so the cost is nil and a stale memo would show links
  // the server refuses.
  const grant = effectiveGrant(state.data);

  // staff-person-login-studio-switcher (2026-09-27) — what this tab is doing
  // now that whoami has landed: rendering a page inside a studio, or showing
  // the chooser because the server resolved none (`studio: null`). The third
  // answer, `canonicalize`, never reaches a render: the whoami handler below
  // acts on it with a full navigation and leaves the state loading. `carried`
  // is the deep link the pick must land on, read off the RAW `window.location`
  // (the router's has the basename stripped and cannot tell a prefixed tab
  // from an un-prefixed one); pure and cheap, so re-derived per render.
  const chooser =
    state.data !== null && state.data.studio === null
      ? { data: state.data, carried: carriedInnerPath(window.location) }
      : null;
  const choosing = chooser !== null;
  // The studios this person could switch TO — every membership but the one
  // this tab is in. Empty for single-studio staff (no switcher) and while
  // choosing (the page IS the chooser; a second list would be noise).
  const otherStudios: ReadonlyArray<WhoamiResponse["studios"][number]> =
    state.data !== null && state.data.studio !== null
      ? state.data.studios.filter((s) => s.id !== state.data?.studio?.id)
      : [];

  // Outside a studio there is nothing to navigate to: the nav lists are
  // empty, the mobile tabs hide, and only the identity + Logout controls
  // remain — the chooser is the whole page.
  const primaryGroups = choosing
    ? []
    : PRIMARY_NAV_GROUPS.map((g) => visibleNav(g, grant))
        // A group whose every entry was filtered out must not leave a dangling
        // divider behind it.
        .filter((g) => g.length > 0);
  // Filtered per group, and a group with nothing left is dropped whole, so a
  // restricted viewer never sees a heading over an empty column.
  const moreGroups: ReadonlyArray<MoreNavGroup> = choosing
    ? []
    : MORE_NAV_GROUPS.map((g) => ({
        heading: g.heading,
        items: visibleNav(g.items, grant),
      })).filter((g) => g.items.length > 0);
  const moreNav = moreGroups.flatMap((g) => g.items);
  const mobileMoreNav = choosing ? [] : visibleNav(MOBILE_MORE_NAV, grant);

  const mobileTabAllowed = (to: string): boolean => {
    if (choosing) return false;
    const page = pageForRoute(to);
    return page === null ? true : pageAllowed(grant, page.id);
  };

  const moreActive = moreNav.some((n) =>
    location.pathname.startsWith(n.to),
  );

  // The mobile "More" tab owns every route that isn't Calendar or Members.
  const mobileMoreActive = !Array.from(MOBILE_TAB_TARGETS).some((to) =>
    location.pathname.startsWith(to),
  );

  // The lost-session bounce (`session-lost.ts`) navigates through the router
  // while this layout is mounted; unregistered on unmount so a later loss
  // outside the app falls back to a full navigation.
  useEffect(
    () => registerSessionLostNavigator((path) => navigate(path, { replace: true })),
    [navigate],
  );

  useEffect(() => {
    let cancelled = false;
    void api
      .whoami()
      .then((data) => {
        if (cancelled) return;
        // staff-person-login-studio-switcher (2026-09-27): the server resolved
        // a studio but this tab's URL carries no `/s/<slug>/` prefix — a
        // single-studio person at `/`, or an un-prefixed deep link the server
        // could auto-resolve. Move the tab to its canonical URL with a FULL
        // load (never `navigate`: `main.tsx` reads the basename once at boot),
        // keeping the path, search and hash. The state stays `loading` so
        // nothing paints under a URL the tab is about to leave.
        // staff-person-login-s2b (2026-09-27): the per-device quick-login list
        // on /login — the address and the studios the SERVER just listed. Every
        // successful whoami refreshes it, so a studio the person left drops
        // off and a new one appears without any client-side guess.
        rememberLogin(
          browserStorage(),
          {
            identifier: data.email,
            studios: data.studios.map((s) => ({ slug: s.slug, name: s.name })),
          },
          new Date().toISOString(),
        );
        const decision = chooserDecision(data, window.location);
        if (decision.kind === "canonicalize") {
          window.location.replace(decision.to);
          return;
        }
        if (data.studio !== null) {
          // Pin the studio timezone BEFORE the state flip that unlocks
          // <Outlet /> — every page's date/time formatting reads it via
          // `studio-tz`, so no instant may render before it's set.
          setStudioTimeZone(data.studio.timezone);
          // Pin the cancellation window too (sibling snapshot) so the
          // calendar's late-drag detection is ready before any page renders.
          setCancellationDeadlineHours(data.studio.cancellation_deadline_hours);
        }
        // `studio: null` = the chooser; nothing to pin, no page will render.
        setState({ loading: false, data, error: null });
      })
      .catch((e: Error) => {
        if (cancelled) return;
        // Lost-session detection: the server rejected the token (401 — the
        // session was signed out elsewhere, expired, or is malformed) OR the
        // person can act in NO studio (403 `studio_unresolved`: no allowlist
        // row names their address; or the studio row is gone after a
        // db:reset / admin-side replacement). Either way: clear the cache,
        // sign out THIS device, bounce to /login with a banner so the user
        // gets a fresh session. `studio_unselected` never arrives here —
        // /api/whoami answers 200 + `studio: null` for it (the chooser).
        // Before staff-session-slice-1 only the 403 codes took this path; a
        // 401 rendered every page with a dead token and each page's cache
        // hook re-fetched on every tick (the 2026-09-27 storm).
        if (e instanceof ApiError) {
          const loss = classifySessionLoss(e.status, e.code);
          if (loss !== null) {
            void handleSessionLost(loss.reason);
            return;
          }
        }
        setState({ loading: false, data: null, error: e.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Logout = THIS device only (`scope: 'local'`). The supabase-js default is
  // `global`, which on 2026-09-27 let one tap on a phone delete every session
  // of the same auth user — including the desktop's on the other host. The
  // global sign-out survives as its own, explicitly labelled action.
  // Both go through `session-lost.ts`, which clears the client cache first
  // (phase 67, sub-decision E) so a later login never renders this session's data.
  const logout = (): Promise<void> => signOutAndLeave("local");
  const logoutEverywhere = (): Promise<void> => signOutAndLeave("global");

  return (
    <WhoamiContext.Provider value={state}>
      <RealtimeProvider studioId={state.data?.studio?.id ?? null}>
      <div className="layout">
        {/* `topbar-bar-open` unfolds the desktop bar above the nav pills. The
            studio chooser has no pills (so no icon to open it with) and its
            only controls ARE that bar's, so it is always open there. */}
        <header className={`topbar${barOpen || choosing ? " topbar-bar-open" : ""}`}>
          {/* The Clavis wordmark: the drawn logo from Mario's kit (2026-09-28),
              the same inline SVG useclavis.ai's masthead and the demo dashboard
              carry, stroked in currentColor so it takes the chrome ink in both
              themes. A named image, so assistive tech reads "Clavis" once. */}
          <div className="topbar-brand">
            <span className="topbar-wordmark" role="img" aria-label="Clavis">
              <svg viewBox="0 0 550 100" aria-hidden="true" focusable="false">
                <g fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="butt" strokeLinejoin="miter">
                  <path d="M81.1 18.9 A44 44 0 1 0 81.1 81.1" />
                  <path d="M131 -10 V94 H185" />
                  <path d="M217.3 112 L255 16 L292.7 112" />
                  <path d="M322.3 -12 L360 84 L397.7 -12" />
                  <path d="M441 -10 V110" />
                  <path transform="translate(13 0)" d="M527 18 C522 9 513 6 503 6 C489 6 477 14 477 27 C477 42 492 46 504 49 C518 52 531 58 531 72 C531 86 519 94 503 94 C491 94 480 89 475 79" />
                </g>
              </svg>
            </span>
          </div>
          {state.data ? (
            <TopbarStudio data={state.data} others={otherStudios} />
          ) : null}
          {choosing ? null : (
          <nav className="topnav">
            {primaryGroups.map((group, groupIndex) => {
              const links = group.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end ?? false}
                  className="topnav-link"
                >
                  {n.label}
                </NavLink>
              ));
              return (
                <Fragment key={group[0]?.to ?? groupIndex}>
                  {groupIndex > 0 ? (
                    <span className="topnav-divider" aria-hidden="true" />
                  ) : null}
                  {/* A group of two or more pages wears one thin outline
                      around its pills (Mario, 2026-10-24: "one around members
                      and calendars"); a page on its own — Home — stands bare,
                      like More and the icon pill. */}
                  {links.length > 1 ? (
                    <div className="topnav-group">{links}</div>
                  ) : (
                    links
                  )}
                </Fragment>
              );
            })}
            <span className="topnav-divider" aria-hidden="true" />
            <div className="nav-more" ref={moreRef}>
              <button
                type="button"
                className={`topnav-link nav-more-btn${
                  moreActive ? " active" : ""
                }${moreOpen ? " open" : ""}`}
                aria-haspopup="true"
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen((open) => !open)}
              >
                More <span aria-hidden="true">▾</span>
              </button>
              {moreOpen ? (
                <div className="nav-more-menu">
                  {moreGroups.map((g) => (
                    <div
                      key={g.heading}
                      className="nav-more-group"
                      role="group"
                      aria-label={g.heading}
                    >
                      <div className="nav-more-heading" aria-hidden="true">
                        {g.heading}
                      </div>
                      {g.items.map((n) => (
                        <NavLink
                          key={n.to}
                          to={n.to}
                          className="nav-more-item"
                          onClick={() => setMoreOpen(false)}
                        >
                          <span className="nav-more-item-label">{n.label}</span>
                          <span className="nav-more-item-desc">
                            {n.description}
                          </span>
                        </NavLink>
                      ))}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            {/* Opens and folds the bar above — the studio, the theme, the
                version, who is signed in and Logout. A person, because
                that bar is the account's. */}
            <button
              type="button"
              className="topnav-link topnav-bar-btn"
              aria-label="Studio and account bar"
              aria-pressed={barOpen}
              title={barOpen ? "Hide the top bar" : "Show the top bar"}
              onClick={toggleBar}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <circle cx="8" cy="5.1" r="2.6" />
                <path d="M2.9 13.8a5.1 5.1 0 0 1 10.2 0" />
              </svg>
            </button>
          </nav>
          )}
          <div className="topbar-user">
            {/* First in the block on purpose: `.topbar-user` is pushed right by
                an auto margin, so a new FIRST child grows the block leftwards
                and leaves the chip, the address and the buttons where they were. */}
            <ThemeToggle />
            <VersionChip />
            {state.data ? (
              <>
                <div className="topbar-who">
                  <span className="topbar-avatar" aria-hidden="true">
                    {emailInitials(state.data.email)}
                  </span>
                  <span>{state.data.email}</span>
                </div>
                {/* The login page is also the studio overview — this device's
                    saved studios, each openable while a session is held
                    (staff-person-login-your-studios, 2026-09-27). A router
                    link: under a studio prefix it resolves to
                    `/s/<slug>/login`, which renders the same page. */}
                <Link
                  className="topbar-logout topbar-studios-link"
                  to="/login"
                  title="Your saved studios — open another, or sign in as someone else"
                >
                  Your studios
                </Link>
                <button className="topbar-logout" onClick={() => void logout()}>
                  Logout
                </button>
                <button
                  className="topbar-logout topbar-logout-all"
                  title="Sign out of every device and browser, not just this one"
                  onClick={() => void logoutEverywhere()}
                >
                  Sign out everywhere
                </button>
              </>
            ) : (
              <span>{state.loading ? "…" : (state.error ?? "")}</span>
            )}
          </div>

          {/* Mobile-only 3-tab bar. CSS hides it above 768px, and hides the
              desktop brand/nav/user below 768px — so exactly one nav shows at
              any width. "More" opens a sheet holding everything else. */}
          <nav className="mobile-tabbar" aria-label="Primary">
            {/*
              These two tabs are hardcoded rather than mapped, so they do NOT
              inherit the filtering applied to the nav arrays above — filtering
              only those would leave a denied Calendar one tap away on a phone.
              Gated individually against the same grant.
            */}
            {mobileTabAllowed("/calendar") ? (
              <NavLink to="/calendar" className="mobile-tab">
                Calendar
              </NavLink>
            ) : null}
            {mobileTabAllowed("/members") ? (
              <NavLink to="/members" className="mobile-tab">
                Members
              </NavLink>
            ) : null}
            <button
              type="button"
              className={`mobile-tab mobile-tab-more${
                mobileMoreActive ? " active" : ""
              }${mobileMoreOpen ? " open" : ""}`}
              aria-haspopup="true"
              aria-expanded={mobileMoreOpen}
              onClick={() => setMobileMoreOpen((open) => !open)}
            >
              More <span aria-hidden="true">▾</span>
            </button>
          </nav>

          {mobileMoreOpen ? (
            <div className="mobile-more-sheet" role="menu">
              <div className="mobile-more-grid">
                {mobileMoreNav.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className="mobile-more-item"
                    onClick={() => setMobileMoreOpen(false)}
                  >
                    {n.label}
                  </NavLink>
                ))}
              </div>
              {/* The phone's studio section: what the desktop masthead's
                  studio and user blocks carry, both hidden below 769px — the
                  studio's face with the theme switch at the row's right end,
                  then the switcher (mobile-more-studio-row, 2026-10-02; the
                  face and the switch sat in a strip above the tabs until
                  then). Real anchors, full navigation — see <TopbarStudio>
                  for why. */}
              {state.data ? (
                <div className="mobile-more-studios">
                  <div className="mobile-more-studio-row">
                    <StudioFace studio={state.data.studio} />
                    <ThemeToggle />
                  </div>
                  {otherStudios.length > 0 ? (
                    <>
                      <div className="mobile-more-studios-label">Switch studio</div>
                      <div className="mobile-more-grid">
                        {otherStudios.map((s) => (
                          <a
                            key={s.id}
                            className="mobile-more-item"
                            href={studioPath(s.slug, "/")}
                          >
                            {s.name}
                          </a>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}
              <div className="mobile-more-footer">
                {state.data ? (
                  <>
                    <span className="mobile-more-user">
                      {state.data.email}
                      {state.data.studio !== null
                        ? ` · ${state.data.studio.name}`
                        : ""}
                    </span>
                    <Link className="mobile-more-studios-link" to="/login">
                      Your studios
                    </Link>
                    <button onClick={() => void logout()}>Logout</button>
                    <button
                      className="mobile-more-logout-all"
                      title="Sign out of every device and browser, not just this one"
                      onClick={() => void logoutEverywhere()}
                    >
                      Sign out everywhere
                    </button>
                  </>
                ) : (
                  <span>{state.loading ? "…" : (state.error ?? "")}</span>
                )}
              </div>
            </div>
          ) : null}
        </header>
        {mobileMoreOpen ? (
          <div
            className="mobile-more-backdrop"
            aria-hidden="true"
            onClick={() => setMobileMoreOpen(false)}
          />
        ) : null}
        <main className="main">
          {/* Pages render only once whoami resolved — the studio timezone is
              pinned in that handler, and a page that painted earlier would
              format its first batch of times in the default zone.

              Migration 178 — the ROUTE guard. Hiding a nav link stops a click,
              not a typed URL or a bookmark, so the page itself must refuse to
              render. The server refuses its data independently (`page-access.ts`);
              this is what stops a half-drawn page from flashing its chrome and
              then erroring, and what makes the redirect land somewhere useful.

              Ordered AFTER the loading gate on purpose: `grant` is `null`
              (unrestricted) until whoami lands, so guarding first would bounce
              every deep link to `/` for one frame on every page load.

              staff-person-login-studio-switcher — with no studio resolved
              there is no page to guard: the chooser renders in the page's
              place, and every card is a full navigation into a studio. */}
          {state.loading ? null : chooser !== null ? (
            <StudioChooser
              studios={chooser.data.studios}
              requestedSlug={chooser.data.requested_slug}
              carried={chooser.carried}
            />
          ) : (
            <RouteGuard grant={grant} resolved={state.data?.page_access_resolved ?? true}>
              <Outlet />
            </RouteGuard>
          )}
        </main>
      </div>
      </RealtimeProvider>
    </WhoamiContext.Provider>
  );
}

/**
 * The studio's identity in the masthead — and, for a person who is staff of
 * several, the switcher (staff-person-login-studio-switcher, 2026-09-27).
 *
 * Three faces:
 *   • one studio — the wordmark or name, as before;
 *   • several — the same face as a menu button; the menu lists the OTHER
 *     studios as real anchors to `/s/<slug>/`. A plain click is a full
 *     navigation (the basename is read once at boot, so a switch must re-boot
 *     the tab); Cmd/middle-click opens the other studio in a second tab, which
 *     is the point — two studios side by side;
 *   • `studio: null` — the chooser is the page, so this just says so.
 *
 * Desktop only: below 769px the tab bar's media query hides this block with
 * the rest of the desktop masthead, and the More sheet's studio section
 * carries the same face, the theme switch and the switch entries instead.
 * The dropdown reuses the "More ▾" menu's classes (`.nav-more-menu` /
 * `.nav-more-item`) plus its own hook for position, so it is the same menu
 * the nav already has, not a second dropdown skin.
 */
function TopbarStudio({
  data,
  others,
}: {
  readonly data: WhoamiResponse;
  readonly others: ReadonlyArray<WhoamiResponse["studios"][number]>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Outside click closes the menu — the same seam the "More" dropdown uses.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (data.studio === null || others.length === 0) {
    return (
      <div className="topbar-studio">
        <StudioFace studio={data.studio} />
      </div>
    );
  }
  return (
    <div className="topbar-studio" ref={ref}>
      <button
        type="button"
        className={`topbar-studio-btn${open ? " open" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        title="Switch studio"
        onClick={() => setOpen((o) => !o)}
      >
        <StudioFace studio={data.studio} />
        <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <div className="nav-more-menu topbar-studio-menu">
          <div className="topbar-studio-menu-label">Switch studio</div>
          {others.map((s) => (
            <a
              key={s.id}
              className="nav-more-item"
              href={studioPath(s.slug, "/")}
            >
              {s.name}
            </a>
          ))}
          <Link className="nav-more-item topbar-studio-menu-all" to="/login">
            Your studios…
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The studio's face: its wordmark when one is configured (L2
 * `studio.branding.logo_url`), otherwise its name in type — a studio without a
 * logo must still be named — and "Choose a studio" while the tab is in none.
 * One component for the desktop masthead and the phone's More sheet, so the
 * two can never name the studio differently.
 */
function StudioFace({
  studio,
}: {
  readonly studio: WhoamiResponse["studio"];
}): JSX.Element {
  if (studio === null) {
    return <div className="topbar-studio-name">Choose a studio</div>;
  }
  return studio.logo_url !== null ? (
    <img className="topbar-studio-logo" src={studio.logo_url} alt={studio.name} />
  ) : (
    <div className="topbar-studio-name">{studio.name}</div>
  );
}

/** "mario.kaspers@icloud.com" → "MK". Single-word local parts fall back to
 * their first two letters. */
function emailInitials(email: string): string {
  const local = email.split("@")[0] ?? email;
  const words = local.split(/[._\-+]+/).filter((w) => w.length > 0);
  const initials =
    words.length >= 2
      ? words
          .slice(0, 2)
          .map((w) => w.charAt(0))
          .join("")
      : local.slice(0, 2);
  return initials.toUpperCase();
}

function VersionChip(): JSX.Element {
  // `LAYER_PUB_VERSION` is injected from `git rev-parse --short HEAD` at
  // build time (vite.config.ts). The "MVP" fallback covers only builds where
  // git is absent and the value falls back to "unknown" (or is unset) — the
  // date/time beside it already updates per deploy.
  const rawVersion = import.meta.env["LAYER_PUB_VERSION"] as string | undefined;
  const version =
    rawVersion && rawVersion !== "unknown" && rawVersion !== "dev"
      ? rawVersion
      : "MVP";
  const commitDateStr =
    (import.meta.env["LAYER_PUB_COMMIT_DATE"] as string) ??
    new Date().toISOString();
  const buildTimeStr =
    (import.meta.env["LAYER_PUB_BUILD_TIME"] as string) ??
    new Date().toISOString();
  const commitDate = new Date(commitDateStr);
  const builtAt = new Date(buildTimeStr);
  const fmt = (d: Date): string =>
    d.toLocaleString("en-GB", {
      timeZone: studioTimeZone(),
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  // Collapsed to the commit glyph — the build identity is reference data, not
  // something to read on every page. Hover reveals it transiently; a click
  // pins it open so it survives the pointer leaving.
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      className={`version-chip${open ? " open" : ""}`}
      aria-expanded={open}
      aria-label={`Build version ${version}, committed ${fmt(commitDate)}`}
      title={`commit ${version}\ncommit date ${commitDate.toISOString()}\ndev started ${builtAt.toISOString()}`}
      onClick={() => setOpen((v) => !v)}
    >
      {/* The "v" is the collapsed state's whole face AND the expanded string's
          first token — so expanding reads "v <sha> · <date>" continuously
          rather than pushing a separate icon in front of a second "v". */}
      <span className="version-chip-mark" aria-hidden="true">
        v
      </span>
      <span className="version-chip-detail">
        <span className="version-chip-label">{version}</span>
        <span className="version-chip-sep">·</span>
        <span className="version-chip-date">{fmt(commitDate)}</span>
      </span>
    </button>
  );
}
